import { useTheme } from "@/theme/ThemeProvider";
import { focusTarget } from "@/ui/a11y/focusTarget";
import { useCoarseTarget } from "@/ui/intake/useCoarseTarget";
import { CheckIcon } from "phosphor-react-native";
import { useRef } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";

export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type KeyEvent = {
  key?: string;
  repeat?: boolean;
  nativeEvent?: { key?: string; repeat?: boolean };
  preventDefault?: () => void;
};

/** Nächster Index für Pfeiltasten. Ohne Auswahl startet ArrowRight/Down beim ersten Eintrag. */
export function segmentedStep(index: number, count: number, key: string): number | null {
  if (count <= 0) return null;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (key === "ArrowRight" || key === "ArrowDown") {
    if (index < 0) return 0;
    return (index + 1) % count;
  }
  if (key === "ArrowLeft" || key === "ArrowUp") {
    if (index < 0) return count - 1;
    return (index - 1 + count) % count;
  }
  return null;
}

function nextEnabled<T extends string>(
  options: SegmentedOption<T>[],
  index: number,
  key: string,
): number | null {
  let next = segmentedStep(index, options.length, key);
  if (next == null) return null;
  for (let guard = 0; guard < options.length && options[next]?.disabled; guard += 1) {
    const stepped = segmentedStep(next, options.length, key);
    if (stepped == null || stepped === next) return null;
    next = stepped;
  }
  return options[next]?.disabled ? null : next;
}

/**
 * Einzelauswahl mit höchstens wenigen Optionen.
 * Gewählt: Fläche accentBg, 2px-Rahmen, Häkchen. Nicht die Primärbutton-Fläche.
 * Höhe: theme.control.heightTouch (44) auf Native, unter dem Tablet-Breakpoint
 * und bei grobem Zeiger. Feiner Desktop-Zeiger: heightDense.
 * Ungewählt: inputBorder. Pfeiltasten wandern mit roving tabindex.
 */
export function SegmentedChoice<T extends string>({
  options,
  value,
  onChange,
  accessibilityLabel,
  style,
  layout = "equal",
  idleBorderColor,
}: {
  options: SegmentedOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  accessibilityLabel: string;
  style?: ViewStyle;
  /** equal teilt die Breite. wrap lässt jede Option in ihrer Textbreite und bricht um. */
  layout?: "equal" | "wrap";
  /** Ungewählt. Standard ist inputBorder. */
  idleBorderColor?: string;
}) {
  const { theme } = useTheme();
  const coarse = useCoarseTarget();
  const height = coarse ? theme.control.heightTouch : theme.control.heightDense;
  const selectedIndex = options.findIndex((option) => option.value === value);
  const tabStop = selectedIndex >= 0 ? selectedIndex : 0;
  const optionRefs = useRef<(View | null)[]>([]);
  const idle = idleBorderColor ?? theme.color.inputBorder;

  const onOptionKey = (index: number, event: KeyEvent) => {
    if (event.repeat ?? event.nativeEvent?.repeat) return;
    const key = event.key ?? event.nativeEvent?.key;
    if (key === " " || key === "Spacebar" || key === "Enter") {
      const option = options[index];
      if (!option || option.disabled) return;
      event.preventDefault?.();
      onChange(option.value);
      focusTarget(optionRefs.current[index], true);
      return;
    }
    if (!key) return;
    const next = nextEnabled(options, index, key);
    if (next == null || next === index) return;
    const option = options[next];
    if (!option) return;
    event.preventDefault?.();
    onChange(option.value);
    focusTarget(optionRefs.current[next], true);
  };

  return (
    <View
      accessibilityRole="radiogroup"
      accessibilityLabel={accessibilityLabel}
      style={[
        {
          flexDirection: "row",
          flexWrap: layout === "wrap" ? "wrap" : "nowrap",
          alignItems: "center",
          gap: theme.space.sm,
          maxWidth: "100%",
        },
        style,
      ]}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        const wrap = layout === "wrap";
        return (
          <Pressable
            key={option.value}
            ref={(node) => {
              optionRefs.current[index] = node;
            }}
            disabled={option.disabled}
            accessibilityRole="radio"
            accessibilityLabel={option.label}
            accessibilityState={{ checked: selected, disabled: Boolean(option.disabled) }}
            {...(Platform.OS === "web"
              ? {
                  "aria-checked": selected,
                  tabIndex: option.disabled || index !== tabStop ? -1 : 0,
                  onKeyDown: (event: KeyEvent) => onOptionKey(index, event),
                }
              : {})}
            onPress={() => {
              if (option.disabled) return;
              onChange(option.value);
            }}
            style={{
              flexGrow: wrap ? 0 : 1,
              flexShrink: wrap ? 0 : 1,
              flexBasis: wrap ? "auto" : 0,
              minHeight: height,
              minWidth: height,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              overflow: wrap ? "visible" : "hidden",
              gap: theme.space.dense,
              paddingHorizontal: theme.space.sm,
              borderRadius: theme.radius.control,
              borderWidth: selected ? 2 : 1,
              borderColor: selected ? theme.color.primary : idle,
              backgroundColor: selected ? theme.color.accentBg : "transparent",
              opacity: option.disabled ? 0.5 : 1,
            }}
          >
            {selected ? (
              <CheckIcon size={14} color={theme.color.primary} weight="bold" />
            ) : null}
            <Text
              numberOfLines={1}
              style={{
                color: theme.color.text,
                fontSize: theme.fontSize.label,
                fontWeight: "600",
                flexShrink: wrap ? 0 : 1,
              }}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
